import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { sectionRenderer } from '@theme/section-renderer';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';

/**
 * A custom element that renders a paginated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {AbortController | undefined} */
  #loadMoreController;

  /** @type {AbortController | undefined} */
  #filterListeners;

  /** @type {PaginatedListAspectRatioHelper | undefined} */
  #loadMoreAspectRatioHelper;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    if (this.getAttribute('loading-mode') === 'click') {
      this.#filterListeners?.abort();
      this.#filterListeners = new AbortController();
      const options = { signal: this.#filterListeners.signal };
      document.addEventListener(StandardEvents.collectionUpdate, this.#cancelForFilterUpdate, options);
      window.addEventListener('popstate', this.#cancelLoadMore, options);
      const templateCard = this.querySelector('[ref="cardGallery"]');
      if (templateCard instanceof HTMLElement) {
        this.#loadMoreAspectRatioHelper = new PaginatedListAspectRatioHelper({ templateCard });
      }
    }
  }

  updatedCallback() {
    super.updatedCallback();
    this.#cancelLoadMore();
  }

  disconnectedCallback() {
    this.#cancelLoadMore();
    this.#filterListeners?.abort();
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Append the next collection page while keeping the current cards and filters.
   * @param {MouseEvent} event - The next-page link activation.
   */
  async loadMore(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target;
    const grid = this.refs.grid;
    const controls = this.querySelector('[data-load-more]');
    if (!(link instanceof HTMLAnchorElement) || !grid || !(controls instanceof HTMLElement)) return;
    event.preventDefault();
    if (this.#loadMoreController) return;

    const controller = new AbortController();
    this.#loadMoreController = controller;
    const context = window.location.href;
    const url = new URL(link.href);
    const label = link.querySelector('[data-load-more-label]');
    const count = controls.querySelector('[data-load-more-count]');
    const errorMessage = controls.querySelector('[data-load-more-error]');
    const progress = controls.querySelector('[data-load-more-progress]');
    const defaultLabel = label?.textContent ?? '';
    const firstNewIndex = grid.querySelectorAll(':scope > [data-product-id]').length;
    const keyboardActivation = event.detail === 0;
    link.setAttribute('aria-disabled', 'true');
    controls.setAttribute('aria-busy', 'true');
    if (label) label.textContent = controls.dataset.loadingLabel ?? defaultLabel;
    if (errorMessage instanceof HTMLElement) errorMessage.hidden = true;

    try {
      const html = await sectionRenderer.getSectionHTML(this.sectionId, false, url, controller.signal);
      if (controller.signal.aborted || !this.isConnected || context !== window.location.href || this.refs.grid !== grid) return;
      const response = new DOMParser().parseFromString(html, 'text/html');
      const section = response.getElementById(`shopify-section-${this.sectionId}`);
      const nextGrid = section?.querySelector('[ref="grid"]');
      const nextControls = section?.querySelector('[data-load-more]');
      if (!nextGrid || !nextControls) throw new Error('Missing collection in next-page response');
      const cards = [...nextGrid.querySelectorAll(':scope > [data-product-id]')];
      const existingIds = new Set([...grid.querySelectorAll(':scope > [data-product-id]')].map((card) => card.getAttribute('data-product-id')));
      const newCards = cards.filter((card) => !existingIds.has(card.getAttribute('data-product-id')));
      if (!newCards.length) throw new Error('No new collection products in next-page response');

      const stylesheet = section?.querySelector('style[data-section-stylesheet]');
      const liveSection = this.closest('.shopify-section');
      if (stylesheet && liveSection && ![...liveSection.querySelectorAll('style[data-section-stylesheet]')].some((style) => style.textContent === stylesheet.textContent)) {
        liveSection.append(stylesheet);
      }
      grid.append(...newCards);
      this.#loadMoreAspectRatioHelper?.processNewElements();
      const displayed = firstNewIndex + newCards.length;
      const total = Number(controls.dataset.total);
      if (count) count.textContent = (controls.dataset.countTemplate ?? '').replace('__SHOWN__', String(displayed));
      if (progress instanceof HTMLElement) progress.style.setProperty('--collection-load-more-progress', String(Math.min(displayed / total, 1)));
      const nextLink = nextControls.querySelector('[data-load-more-next]');
      if (nextLink instanceof HTMLAnchorElement) {
        link.href = nextLink.getAttribute('href') ?? '';
      } else {
        link.hidden = true;
      }
      if (keyboardActivation) {
        const firstLink = newCards[0]?.querySelector('a[href]');
        if (firstLink instanceof HTMLElement) firstLink.focus({ preventScroll: true });
      }
    } catch (error) {
      if (!controller.signal.aborted && context === window.location.href && errorMessage instanceof HTMLElement) {
        errorMessage.hidden = false;
      }
    } finally {
      if (this.#loadMoreController === controller) {
        this.#loadMoreController = undefined;
        link.removeAttribute('aria-disabled');
        controls.removeAttribute('aria-busy');
        if (label) label.textContent = defaultLabel;
      }
    }
  }

  #cancelLoadMore = () => {
    this.#loadMoreController?.abort();
    this.#loadMoreController = undefined;
    const controls = this.querySelector('[data-load-more]');
    controls?.removeAttribute('aria-busy');
    const link = controls?.querySelector('[data-load-more-next]');
    link?.removeAttribute('aria-disabled');
    const label = link?.querySelector('[data-load-more-label]');
    if (label && controls instanceof HTMLElement) label.textContent = controls.dataset.buttonLabel ?? '';
  };

  /** @param {Event} event - Collection filtering or sorting update. */
  #cancelForFilterUpdate = (event) => {
    const source = event.target;
    if (source instanceof Element && source.closest('.shopify-section')?.id === `shopify-section-${this.sectionId}`) {
      this.#cancelLoadMore();
    }
  };

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
